import {Component, Inject, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {IPortfolioResultResponse} from '@irb/irb-editors/editor-portfolio-result/editor-portfolio-result.model';
import {FormControl, FormGroup, ValidatorFn} from '@angular/forms';
import {AuthService} from '@irb-common/services/auth.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import {IrbAgGridExportService} from '@irb-common/components/irebal-ag-grid-ent-avatar/irb-ag-grid-export/irb-ag-grid-export.service';

export interface ExportPortfolioResults {
    rebalanceId: number;
    rebalanceName: string;
    rebalanceType: string;
    customCategoryName: string;
    selectedPortfolios: Partial<IPortfolioResultResponse>[];
    isDetail: boolean;
}

@Component({
  selector: 'irb-dialog-pr-export-pdf-legacy',
  standalone: false,
  templateUrl: './dialog-pr-export-pdf-legacy.component.html',
  styleUrl: './dialog-pr-export-pdf-legacy.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class DialogPrExportPdfLegacyComponent {
  formGroup: FormGroup;
  positionsWithNoOrders: FormControl;
  allocationsDisplay: FormControl;
  allocationsGroupBy: FormControl;
  allocationsVariance: FormControl;
  allocationsExcludedPositions: FormControl;
  allocationChartGroupBy: FormControl;

  constructor(
      private authService: AuthService,
      private dialogRef: MatDialogRef<DialogPrExportPdfLegacyComponent>,
      private _http: HttpClient,
      @Inject(MAT_DIALOG_DATA) public data: ExportPortfolioResults,
      private ges: IrbAgGridExportService
  ) {
      this.formGroup = new FormGroup({
          includeSummary: new FormControl(true),
          includeCashValues: new FormControl(true),
          includeOrders: new FormControl(true),
          includeAllocations: new FormControl(true),
          includeAllocationChart: new FormControl(true)
      }, this.formGroupValidator);

      this.positionsWithNoOrders = new FormControl('HIDE');
      this.allocationsDisplay = new FormControl('PERCENT');
      this.allocationsGroupBy = new FormControl('POSITION');
      this.allocationsVariance = new FormControl('REL');
      this.allocationsExcludedPositions = new FormControl('SHOW');
      this.allocationChartGroupBy = new FormControl('SEC_CATEGORY');
  }

  get isDetail(): boolean {
      return this.data && this.data.isDetail;
  }

  get isSummary(): boolean {
      return !this.isDetail;
  }

  get isFormValid(): boolean {
      return this.formGroup && this.formGroup.valid;
  }

  close() {
      this.dialogRef.close();
  }

  continue() {
      const payload = {
          isDetail: this.data.isDetail,
          jasperRequest: {
              rebalPkgId: this.data.rebalanceId,
              rebalName: this.data.rebalanceName,
              rebalType: this.data.rebalanceType,
              portfolioList: this.data.selectedPortfolios.map(portfolio => ({
                  portfolioId: portfolio.portfolioId,
                  portfolioName: portfolio.portfolioName
              })),
              jasperSettingDto: this.getJasperSettings()
          }
      }
      
      this.getJasperReport(payload).subscribe((_res: any) => {
          this.ges.showSuccessMessage();
      });

      this.dialogRef.close();
  }

  private formGroupValidator: ValidatorFn = () => {
      if (!this.formGroup) {
          return null;
      }
      for (let control in this.formGroup.controls) {
          if (this.formGroup.controls[control].value === true) {
              return null;
          }
      }
      return {
          noSectionsSelected: true
      }
  }

  private getJasperSettings() {
      return {
          showSummaryReport: this.formGroup.get('includeSummary').value,
          showCashReport: this.formGroup.get('includeCashValues').value,
          showOrderReport: this.formGroup.get('includeOrders').value,
          showAllOrder: this.positionsWithNoOrders.value === 'SHOW',
          showAllcnReport: this.formGroup.get('includeAllocations').value,
          showAllcnDollarPercent: this.allocationsDisplay.value === 'DOLLAR',
          allcnGroup: this.allocationsGroupBy.value,
          allcnGroupCCName: this.data.customCategoryName,
          showAllcnAbsVar: this.allocationsVariance.value === 'ABS',
          showAllcnExcludedPositions: this.allocationsExcludedPositions.value === 'SHOW',
          showAllcnChartReport: this.formGroup.get('includeAllocationChart').value,
          chartGroup: this.allocationChartGroupBy.value,
          chartGroupCCName: this.data.customCategoryName
      }
  }

  getJasperReport(data: any): Observable<Object> {
      const headers = new HttpHeaders({
          'Accept': 'application/pdf',
          'Content-Type': 'application/json'
      });

      return this._http.post("/irebalone/async/get-jasper-report", JSON.stringify(data), { headers: headers });
  }
}
